import {Component, effect, inject, input, signal} from '@angular/core';
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {LineChartModule} from "@swimlane/ngx-charts";
import {Location, NgClass} from "@angular/common";
import {FixedAsset, LoRaGateway, SynchronizationStatus} from "src/app/infrastructure/moovement-api/models/asset";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {ActionType, DataType} from "src/app/domain/domain-models";
import {Subject, takeUntil} from 'rxjs';
import {GatewaySvgComponent} from "src/app/presentation/common/icons/gateway-svg/gateway-svg.component";
import {NavBarStateStore} from 'src/app/presentation/main-view/nav-bar/nav-bar-state/nav-bar-state.store';
import {
  dropAPinMessage,
  InternalNotification,
  InternalNotificationsStore,
  InternalNotificationType
} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {MainViewStore} from 'src/app/presentation/main-view/main-view.store';
import {GatewayStore} from 'src/app/domain/devices/gateway/gateway.store';
import {GatewayStatusStore} from 'src/app/domain/devices/gateway/gateway-status.store';
import {toGatewayColorClass, toGatewayMessage} from 'src/app/domain/devices/gateway/gateway';
import {randomUuid} from 'src/app/domain/util/uuid/uuid';
import {parseOptionalFloat} from 'src/app/domain/util/parsing/parsing';
import {UserStore} from 'src/app/domain/user/user.store';

@Component({
  selector: 'app-customize-gateway',
  imports: [
    GenericButtonComponent,
    LineChartModule,
    NgClass,
    GatewaySvgComponent
],
  templateUrl: './customize-gateway.component.html',
  styleUrl: './customize-gateway.component.css'
})
export class CustomizeGatewayComponent {

  id = input.required<string>();

  canBeAltered: boolean = false;
  canBeDeleted: boolean = false;
  gateway = signal<LoRaGateway | undefined>(undefined);
  loading = false;
  status = signal<string | undefined>(undefined);
  statusMessage = signal('Last seen: ?');
  statusMessageColorClass = signal('color-danger');

  private lat?: number;
  private long?: number;

  private $destroyDropAPin: Subject<void> = new Subject<void>();
  private dropAPinNotification?: InternalNotification;

  private gatewayStore = inject(GatewayStore);
  private gatewayStatusStore = inject(GatewayStatusStore);
  private internalNotificationsStore = inject(InternalNotificationsStore);
  private location = inject(Location);
  private mainViewStore = inject(MainViewStore);
  private menuStore = inject(NavBarStateStore);
  private userStore = inject(UserStore);

  constructor(
    private dataSynchronisationService: DataSynchronisationService,
    private dataSubscriptionService: DataSubscriptionService,
    protected viewportService: ViewportService
  ) {
    effect(() => {
      const coordinates = this.gateway()?.location?.coords;
      this.lat = coordinates ? coordinates[0] : undefined;
      this.long = coordinates ? coordinates[1] : undefined;
    });

    effect(() => {
      this.gateway.set(this.gatewayStore.entityMap()[this.id()]);
      this.dataSubscriptionService.publish(this.id() + '.focus', randomUuid());
    });

    effect(() => {
      this.canBeAltered = this.userStore.canEditPersistable(this.gateway());
      this.canBeDeleted = this.userStore.canDeletePersistable(this.gateway());
    });

    effect(() => {
      const status = this.gatewayStatusStore.entityMap()[this.id()];
      if (!status) return;

      this.status.set(status.normalisedValue);
      this.statusMessage.set(toGatewayMessage(status.normalisedValue, status.instant));
      this.statusMessageColorClass.set(toGatewayColorClass(status.normalisedValue));
    });
  }

  setName($event: any): void {
    const name = $event.target.value;
    if (!name) return;

    this.gateway.update(gateway => {
      if (!gateway) return undefined;

      return {
        ...gateway,
        name
      }
    });
  }

  setLatitude($event: any): void {
    this.lat = parseOptionalFloat($event.target.value);
    this.updateCoordinates();
  }

  setLongitude($event: any) {
    this.long = parseOptionalFloat($event.target.value);
    this.updateCoordinates();
  }

  private updateCoordinates() {
    if (!this.lat || !this.long) return;

    this.gateway.update(gateway => {
      if (!gateway) return undefined;

      return {
        ...gateway,
        location: {
          ...gateway.location,
          coords: [this.lat!, this.long!]
        }
      }
    });
  }

  async applyChanges() {
    if (!this.loading && this.canBeAltered) {
      await this.dataSynchronisationService.applyAction(
        DataType.loRaGateways,
        ActionType.mergeAsset,
        this.gateway()!,
        SynchronizationStatus.syncPending
      );
    }
  }

  onDropAPin() {
    this.menuStore.disable();
    this.dropAPinNotification = this.internalNotificationsStore.addNotification(dropAPinMessage, InternalNotificationType.INFO, true);
    this.dataSubscriptionService.publish('dropAPin', true)
    this.dataSubscriptionService.onFirst<{
      lng: number,
      lat: number
    }>('currentPosition').pipe(takeUntil(this.$destroyDropAPin)).subscribe({
      next: data => {
        if (confirm("Are you sure you want to change the location of this gateway?")) {
          this.gateway.update(gateway => {
            if (!gateway) return undefined;

            return {
              ...gateway,
              location: {
                ...(gateway as FixedAsset)?.location,
                coords: [data.lat, data.lng]
              }
            }
          });
          this.applyChanges()
        }
        this.reset();
      }
    })
    this.mainViewStore.hideChildComponent();
  }

  private reset() {
    this.menuStore.enable();
    this.dataSubscriptionService.publish('currentPosition');
    this.$destroyDropAPin.complete();
    this.$destroyDropAPin = new Subject<void>();
    if (this.dropAPinNotification) this.internalNotificationsStore.deleteNotification(this.dropAPinNotification);
    this.dataSubscriptionService.publish('dropAPin', false);
    this.mainViewStore.showChildComponent();
  }

  back(): void {
    this.location.back();
  }

  close() {
    this.mainViewStore.hideChildComponent();
  }
}
